"use client";

import { useRoutePathname } from "@repo/statsig";
import { VaulModal } from "@repo/ui";
import { useStatsigClient, type RegisteredStatsigEvent } from "@repo/statsig";
import * as Sentry from "@sentry/nextjs";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import useMeasure from "react-use-measure";
import { twMerge } from "tailwind-merge";

import { usePaymentComplete } from "src/lib/use-payment-complete";
import type {
  BillingPeriod,
  GateStage,
  ProductKey,
  SubscriptionGateModalProps,
} from "../types";
import { ANIMATION, ANALYTICS_EVENTS, GATE_STAGES } from "../constants";
import { PaywallModalContent } from "./paywall-modal-content";
import { PaymentModalContent } from "./payment-modal-content";

/**
 * Subscription gate modal with paywall and payment stages
 *
 * Uses VaulModal for consistent drawer UI with the NPS pattern.
 * Manages two stages: paywall (product selection) and payment (Stripe checkout).
 * Payment completes inline — no redirect.
 */
export function SubscriptionGateModal({
  isOpen,
  onClose,
}: SubscriptionGateModalProps) {
  const [stage, setStage] = useState<GateStage>(GATE_STAGES.PAYWALL);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductKey>("unlimitedRx");
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("yearly");
  const [elementRef, bounds] = useMeasure({
    debounce: { scroll: 0, resize: 0 },
  });
  const [prevHeight, setPrevHeight] = useState(0);

  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStage(GATE_STAGES.PAYWALL);
      setSelectedProduct("unlimitedRx");
      setBillingPeriod("yearly");
      setPrevHeight(0);

      // Log gate opened event
      const statsigEvent: RegisteredStatsigEvent = {
        eventName: ANALYTICS_EVENTS.gateOpened,
        metadata: {
          ...locationData,
        },
      };
      logEvent(statsigEvent);
    }
  }, [isOpen, logEvent, locationData]);

  // Calculate dynamic opacity duration based on height change
  const heightDiff = Math.abs(bounds.height - prevHeight);
  const opacityDuration = Math.min(
    Math.max(
      heightDiff / ANIMATION.HEIGHT_DIFFERENCE_DIVISOR,
      ANIMATION.MIN_DURATION
    ),
    ANIMATION.MAX_DURATION
  );

  useEffect(() => {
    setPrevHeight(bounds.height);
  }, [bounds.height]);

  const handleStartTrial = useCallback(
    (product: ProductKey, period: BillingPeriod) => {
      setSelectedProduct(product);
      setBillingPeriod(period);
      setStage(GATE_STAGES.PAYMENT);

      // Log payment started event
      const statsigEvent: RegisteredStatsigEvent = {
        eventName: ANALYTICS_EVENTS.gatePaymentStarted,
        value: product,
        metadata: {
          ...locationData,
          product,
          billingPeriod: period,
        },
      };
      logEvent(statsigEvent);
    },
    [logEvent, locationData]
  );

  const processPayment = usePaymentComplete();

  const handlePaymentComplete = useCallback(
    async (data: { customerId: string; subscriptionId: string }) => {
      try {
        await processPayment(data);
        onClose();
      } catch (err) {
        Sentry.captureException(err, {
          extra: { customerId: data.customerId },
        });
        console.error("[SubscriptionGateModal] payment complete failed:", err);
        onClose();
      }
    },
    [processPayment, onClose]
  );

  const handleBackToPaywall = useCallback(() => {
    setStage(GATE_STAGES.PAYWALL);
  }, []);

  const handleDismiss = useCallback(() => {
    // Log dismissal event
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: ANALYTICS_EVENTS.gateDismissed,
      metadata: {
        ...locationData,
        stage,
        product: selectedProduct,
        billingPeriod,
      },
    };
    logEvent(statsigEvent);

    onClose();
  }, [logEvent, locationData, stage, selectedProduct, billingPeriod, onClose]);

  const renderStageContent = () => {
    switch (stage) {
      case GATE_STAGES.PAYWALL:
        return (
          <PaywallModalContent onStartTrial={handleStartTrial} mode="modal" />
        );
      case GATE_STAGES.PAYMENT:
        return (
          <PaymentModalContent
            productKey={selectedProduct}
            cadence={billingPeriod}
            onBack={handleBackToPaywall}
            onPaymentComplete={handlePaymentComplete}
          />
        );
      default:
        return null;
    }
  };

  // Custom drawer content styling - floating bottom sheet with margins
  const drawerContentClassname =
    "fixed bottom-[40px] left-[16px] right-[16px] z-[100000] outline-none flex justify-center w-[calc(100%-32px)] max-h-[calc(100vh-80px)]";

  const cardClassName = twMerge(
    "w-full overflow-hidden p-0 rounded-[16px]",
    "bg-teal-1000"
  );

  return (
    <VaulModal
      isOpen={isOpen}
      onClose={handleDismiss}
      dismissible={false}
      drawerContentClassname={drawerContentClassname}
      cardClassName={cardClassName}
      repositionInputs={false}
      fixed={true}
    >
      <motion.div
        animate={{
          height: bounds.height || "auto",
          transition: {
            duration: ANIMATION.HEIGHT_TRANSITION_DURATION,
            ease: [0.25, 1, 0.5, 1],
          },
        }}
        className="overflow-hidden w-full flex items-center justify-center"
      >
        <div ref={elementRef} className="flex flex-col w-full">
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={stage}
              initial={ANIMATION.fadeInOut.initial}
              animate={ANIMATION.fadeInOut.animate}
              exit={ANIMATION.fadeInOut.exit}
              transition={{
                duration: opacityDuration,
                ease: [0.26, 0.08, 0.25, 1],
              }}
            >
              {renderStageContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </VaulModal>
  );
}
